import { __awaiter } from "tslib";
import React, { PureComponent } from 'react';
import styled from 'styled-components';
import createDebug from 'debug';
import { Display } from './display';
import { header } from './header';
import { UserInput, UserPrompt, UserConfirm } from './user_input';
import { command, toArgv, toOptions } from './command';
import { loading } from './command/terminal_buffer';
const debug = createDebug('bb:terminal');
const TerminalContainer = styled.div `
  width: 100%;
  height: 100vh;
  overflow: auto;
  font-family: 'Courier New', Courier, monospace;

  &:focus {
    outline: none;
  }
`;
export class WebTerminal extends PureComponent {
    constructor(props) {
        super(props);
        this.focused = false; // track focused state before app is initialized.
        this.maxNumberOfCommands = 100;
        this.setRef = (ref) => {
            this.container = ref;
        };
        this.handleFocus = () => {
            this.focused = true;
            const { focused, input } = this.state;
            if (focused || !input)
                return;
            input.focus();
            const inputContent = input.getTextArray();
            this.setState({
                focused: true,
                inputContent,
            });
        };
        this.handleBlur = () => {
            this.focused = false;
            const { focused, input } = this.state;
            if (!focused || !input)
                return;
            input.blur();
            const inputContent = input.getTextArray();
            this.setState({
                focused: false,
                inputContent,
            });
        };
        this.handleKeyDown = (e) => {
            const { focused, input } = this.state;
            if (!focused || !input)
                return;
            const { keyCode: code, key } = e;
            if (e.ctrlKey && code === 67) {
                // ^C
                this.addNewCommandLine();
                return;
            }
            if ((e.metaKey || e.ctrlKey) && code === 75) {
                // ⌘K or ^K
                this.restart();
                return;
            }
            if (e.ctrlKey || e.altKey || e.metaKey)
                return;
            let updated = false;
            switch (code) {
                case 8: // delete
                    updated = input.deleteChar();
                    break;
                case 13: // enter
                    this.handleSubmit();
                    break;
                case 37: // arrow left
                    updated = input.moveCursor(-1);
                    break;
                case 38: {
                    // arrow up
                    e.preventDefault();
                    if (input.enableHistory) {
                        updated = this.traceHistory(-1);
                    }
                    break;
                }
                case 39: // arrow right
                    updated = input.moveCursor(1);
                    break;
                case 40: {
                    // arrow down
                    e.preventDefault();
                    if (input.enableHistory) {
                        updated = this.traceHistory(1);
                    }
                    break;
                }
                default:
                    if (key.length === 1) {
                        input.insert(key);
                        updated = true;
                    }
            }
            if (updated) {
                const inputContent = input.getTextArray();
                this.setState({ inputContent }, this.scrollToBottom);
            }
        };
        this.handlePaste = (e) => {
            const { focused, input } = this.state;
            if (!focused || !input)
                return;
            const data = e.clipboardData.getData('text');
            input.insert(data);
            const inputContent = input.getTextArray();
            this.setState({ inputContent });
        };
        this.handleSubmit = () => __awaiter(this, void 0, void 0, function* () {
            const { input } = this.state;
            if (!input)
                return;
            input.blur();
            if (input.stopSubmitPropagation) {
                input.submit();
                return;
            }
            const value = input.getValue().trim();
            if (!value) {
                this.addNewCommandLine();
                return;
            }
            yield this.commitInputValue();
            const { commandHistory: prevCommandHistory } = this.state;
            let commandHistory = prevCommandHistory;
            if (value !== prevCommandHistory[prevCommandHistory.length - 1]) {
                yield this.props.app.addCommand(value);
                commandHistory = [...prevCommandHistory.slice(1 - this.maxNumberOfCommands), value];
            }
            this.setState({
                commandHistory,
            }, () => __awaiter(this, void 0, void 0, function* () { return this.runCommand(value); }));
        });
        this.replaceInputValue = (value) => __awaiter(this, void 0, void 0, function* () {
            const { input } = this.state;
            if (!input)
                return;
            yield new Promise(resolve => {
                input.replace(value);
                const inputContent = input.getTextArray();
                this.setState({ inputContent }, resolve);
            });
        });
        this.log = (newContent) => __awaiter(this, void 0, void 0, function* () {
            yield this.commitInputValue();
            return new Promise(resolve => {
                const { staticContent: prevStaticContent } = this.state;
                const staticContent = [...prevStaticContent, ...newContent];
                this.setState({ staticContent }, () => {
                    this.scrollToBottom();
                    resolve();
                });
            });
        });
        this.removeLog = (numberOfLines = 1) => __awaiter(this, void 0, void 0, function* () {
            return new Promise(resolve => {
                const { staticContent: prevStaticContent } = this.state;
                const staticContent = prevStaticContent.slice(0, prevStaticContent.length - numberOfLines);
                this.setState({ staticContent }, resolve);
            });
        });
        this.prompt = (question) => __awaiter(this, void 0, void 0, function* () {
            return new Promise(resolve => {
                const userPrompt = new UserPrompt(question, '', 0, resolve);
                this.addNewCommandLine(userPrompt);
            });
        });
        this.confirm = (question) => __awaiter(this, void 0, void 0, function* () {
            return new Promise(resolve => {
                const userConfirm = new UserConfirm(question, '', 0, resolve);
                this.addNewCommandLine(userConfirm);
            });
        });
        this.state = {
            staticContent: header,
            focused: false,
            input: null,
            inputContent: [],
            commandHistory: [],
        };
    }
    componentDidMount() {
        return __awaiter(this, void 0, void 0, function* () {
            document.addEventListener('keydown', this.handleKeyDown, true);
            document.addEventListener('paste', this.handlePaste, true);
            const { app } = this.props;
            const loader = loading({
                argv: [],
                options: {},
                replace: this.replaceInputValue,
                log: this.log,
                removeLog: this.removeLog,
                prompt: this.prompt,
                confirm: this.confirm,
            }, 'Initializing data');
            loader.start();
            const startTimestamp = Date.now();
            yield app.init();
            const latestCommands = yield app.getCommands(this.maxNumberOfCommands);
            const commandHistory = latestCommands.map(({ cmd }) => cmd).reverse();
            if (latestCommands.length) {
                const oldestId = latestCommands[latestCommands.length - 1].id;
                yield app.deleteOldCommands(oldestId - 1);
            }
            setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                yield loader.stop();
                if (commandHistory.length) {
                    this.setState({ commandHistory });
                }
                if (this.focused) {
                    this.setState({ focused: true }, () => this.addNewCommandLine());
                }
                else {
                    this.addNewCommandLine();
                }
            }), Math.max(0, 2000 - (Date.now() - startTimestamp))); // Make the loader display for at least 2 secs to avoid flash of the text.
        });
    }
    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown, true);
        document.removeEventListener('paste', this.handlePaste, true);
    }
    commitInputValue() {
        return __awaiter(this, void 0, void 0, function* () {
            const { input } = this.state;
            if (!input)
                return;
            return new Promise(resolve => {
                const newContent = input.getTextArray();
                const { staticContent: prevStaticContent } = this.state;
                const staticContent = [...prevStaticContent, ...newContent];
                this.setState({
                    staticContent,
                    input: null,
                    inputContent: [],
                }, resolve);
            });
        });
    }
    runCommand(value) {
        return __awaiter(this, void 0, void 0, function* () {
            const { app } = this.props;
            const argv = toArgv(value);
            const terminalBuf = {
                argv,
                options: toOptions(argv),
                replace: this.replaceInputValue,
                log: this.log,
                removeLog: this.removeLog,
                prompt: this.prompt,
                confirm: this.confirm,
            };
            try {
                yield command(terminalBuf, app);
            }
            catch (e) {
                debug(e);
                yield this.log([{ text: `${e}`, color: 'red' }]);
            }
            yield this.addNewCommandLine();
        });
    }
    restart() {
        const staticContent = header;
        const input = new UserInput();
        if (this.state.focused) {
            input.focus();
        }
        const inputContent = input.getTextArray();
        this.setState({
            staticContent,
            input,
            inputContent,
        });
    }
    addNewCommandLine(input = new UserInput()) {
        const { input: prevInput, staticContent: prevStaticContent } = this.state;
        let staticContent = prevStaticContent;
        if (prevInput) {
            prevInput.blur();
            const newContent = prevInput.getTextArray();
            staticContent = [...prevStaticContent, ...newContent];
        }
        if (this.state.focused) {
            input.focus();
        }
        const inputContent = input.getTextArray();
        this.setState({
            staticContent,
            input,
            inputContent,
        }, this.scrollToBottom);
    }
    traceHistory(offset) {
        const { input, commandHistory } = this.state;
        if (!input) {
            return false;
        }
        const value = input.getValue();
        if (input.historyIdx < 0) {
            input.historyIdx = commandHistory.length;
            input.historyRoot = value;
        }
        let idx = input.historyIdx + offset;
        for (; idx >= 0 && idx < commandHistory.length; idx += offset) {
            const cmd = commandHistory[idx];
            if (cmd.startsWith(input.historyRoot)) {
                input.historyIdx = idx;
                input.replace(cmd);
                return true;
            }
        }
        if (idx >= commandHistory.length) {
            if (input.historyRoot && input.historyRoot !== value) {
                input.replace(input.historyRoot);
                return true;
            }
            if (value) {
                input.deleteAll();
                return true;
            }
        }
        return false;
    }
    scrollToBottom() {
        if (!this.container)
            return;
        this.container.scrollTop = this.container.scrollHeight;
    }
    render() {
        const { staticContent, inputContent } = this.state;
        const content = [...staticContent, ...inputContent];
        return (React.createElement(TerminalContainer, { ref: this.setRef, onFocus: this.handleFocus, onBlur: this.handleBlur, tabIndex: 0 },
            React.createElement(Display, { content: content })));
    }
}
//# sourceMappingURL=data:application/json;base64,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